import { Box, useBreakpointValue, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import Container from 'components/common/Container';
import ProductListHeader from './ProductsListHeader';
import ProductListSettings from './ProductsListSettings';
import ProductListSettingsMobile from './ProductListSettingsMobile';
import ProductListAccordion from './ProductListAccordion';
import ProductListItems from './ProductListItems';
import MetaTagsHandler from 'components/common/MetaTagsHandler';
import useProducts from 'hooks/useProducts';
import { getProductCategory } from 'services/product';
import useProductListing from 'hooks/useProductListing';
import { useState, useEffect } from 'react';
import ProductListHeaderMobile from './ProductListHeaderMobile';
import { getFormattedFilters } from 'utils/helpers';

export default function ProductListView({ page, data, info, pager, facets, sort, breadcrumbs }) {
  const {
    handleWornSwitch,
    handleLoadMore,
    handleRemoveFilter,
    handleSelectFilter,
    selectedSort,
    selectedFilters,
    loadingMore,
    wornImageActive,
    productCategoryData,
    isValidating,
    setSelectedSort,
  } = useProductListing(page, data, info, pager, facets, sort, useProducts, getProductCategory);
  const [productListHeight, setProductListHeight] = useState();
  const router = useRouter();
  const [formattedFilters, setFormattedFilters] = useState([]);

  useEffect(() => {
    selectedFilters && setFormattedFilters(getFormattedFilters(selectedFilters));
  }, [selectedFilters]);

  useEffect(() => {
    const el = typeof document !== 'undefined' ? document.getElementById('productList') : null;
    if (el) {
      setProductListHeight(el.getBoundingClientRect().height);
    }
  }, [router.asPath, productCategoryData]);

  function createDescriptionMarkup() {
    return { __html: productCategoryData?.info?.description };
  }

  const header = useBreakpointValue({
    base: <ProductListHeaderMobile info={productCategoryData?.info} breadcrumbs={breadcrumbs} />,
    sm: <ProductListHeaderMobile info={productCategoryData?.info} breadcrumbs={breadcrumbs} />,
    md: <ProductListHeaderMobile info={productCategoryData?.info} breadcrumbs={breadcrumbs} />,
    lg: <ProductListHeader info={productCategoryData?.info} breadcrumbs={breadcrumbs} />,
    xl: <ProductListHeader info={productCategoryData?.info} breadcrumbs={breadcrumbs} />,
  });

  const accordion = useBreakpointValue({
    base: <></>,
    sm: <></>,
    md: <></>,
    lg: (
      <ProductListAccordion
        isValidating={isValidating}
        facets={productCategoryData?.facets || []}
        selectedFilters={selectedFilters}
        handleSelectFilter={handleSelectFilter}
      />
    ),
    xl: (
      <ProductListAccordion
        isValidating={isValidating}
        facets={productCategoryData?.facets || []}
        selectedFilters={selectedFilters}
        handleSelectFilter={handleSelectFilter}
      />
    ),
  });

  const productListSettings = useBreakpointValue({
    base: (
      <ProductListSettingsMobile
        facets={productCategoryData?.facets || []}
        handleSelectFilter={handleSelectFilter}
        pager={productCategoryData?.pager}
        sort={productCategoryData?.sort}
        setSelectedSort={setSelectedSort}
        selectedSort={selectedSort}
        selectedFilters={selectedFilters}
        handleRemove={handleRemoveFilter}
        handleWornImage={handleWornSwitch}
        isMobile={true}
        productListHeight={productListHeight}
      />
    ),
    sm: (
      <ProductListSettingsMobile
        facets={productCategoryData?.facets || []}
        handleSelectFilter={handleSelectFilter}
        pager={productCategoryData?.pager}
        sort={productCategoryData?.sort}
        setSelectedSort={setSelectedSort}
        selectedSort={selectedSort}
        selectedFilters={selectedFilters}
        handleRemove={handleRemoveFilter}
        handleWornImage={handleWornSwitch}
        isMobile={true}
        isValidating={isValidating}
        productListHeight={productListHeight}
      />
    ),
    md: (
      <ProductListSettingsMobile
        facets={productCategoryData?.facets || []}
        handleSelectFilter={handleSelectFilter}
        pager={productCategoryData?.pager}
        sort={productCategoryData?.sort}
        setSelectedSort={setSelectedSort}
        selectedSort={selectedSort}
        selectedFilters={selectedFilters}
        handleRemove={handleRemoveFilter}
        handleWornImage={handleWornSwitch}
        isMobile={true}
        isValidating={isValidating}
        productListHeight={productListHeight}
      />
    ),
    lg: (
      <ProductListSettings
        selectedSort={selectedSort}
        pager={productCategoryData?.pager}
        sort={productCategoryData?.sort}
        setSelectedSort={setSelectedSort}
        selectedFilters={selectedFilters}
        handleRemove={handleRemoveFilter}
        handleWornImage={handleWornSwitch}
        isValidating={isValidating}
      />
    ),
    xl: (
      <ProductListSettings
        selectedSort={selectedSort}
        pager={productCategoryData?.pager}
        sort={productCategoryData?.sort}
        setSelectedSort={setSelectedSort}
        selectedFilters={selectedFilters}
        handleRemove={handleRemoveFilter}
        handleWornImage={handleWornSwitch}
        isValidating={isValidating}
      />
    ),
  });

  return (
    <>
      {productCategoryData ? (
        <>
          {formattedFilters?.length > 1 && <meta name="robots" content="noindex" />}
          {productCategoryData?.info?.metaTags && <MetaTagsHandler metaTags={productCategoryData.info.metaTags} />}
          {header}
          <Container>
            <Box className="sbl_i" display={'none'}>
              <span class="category_id">{productCategoryData?.info?.name}</span>
            </Box>
            {productListSettings}
            <Box as="div" display="flex" flexDir="row" mb={'50px'}>
              <Box w={['0', '0', '0%', '30%', '30%']} paddingRight={['0', '0', '0', '25px', '25px']}>
                {accordion}
              </Box>
              <Box
                w={['100%', '100%', '100%', '70%', '70%']}
                opacity={isValidating ? 0.5 : 1}
                transition={'all 0.3s ease-in-out'}
                id="productList"
              >
                {!isValidating && (
                  <ProductListItems
                    isValidating={isValidating}
                    info={productCategoryData?.info}
                    data={productCategoryData?.data}
                    pager={productCategoryData?.pager}
                    loadingMore={loadingMore}
                    handleLoadMore={handleLoadMore}
                    wornActive={wornImageActive}
                    selectedFilters={selectedFilters}
                    handleRemove={handleRemoveFilter}
                  />
                )}
              </Box>
            </Box>
            <Box justifyContent="center" textAlign="center" fontSize="16px" padding="20px 0">
              <Text as={'div'} dangerouslySetInnerHTML={createDescriptionMarkup()} />
            </Box>
          </Container>
        </>
      ) : null}
    </>
  );
}
